export const CAMPAIGN_COMPONENT_GROUP_CATEGORY = {
  BLOCK: 'block',
  FLOWKIT: 'flowkit',
  OTHER: 'other',
  SECTION: 'section',
  UTILITY: 'utility',
} as const;

export type CampaignComponentGroupCategory =
  (typeof CAMPAIGN_COMPONENT_GROUP_CATEGORY)[keyof typeof CAMPAIGN_COMPONENT_GROUP_CATEGORY];

export const CAMPAIGN_COMPONENT_GROUP_NAMES = {
  BLOCKS: 'Blocks',
  FLOWKIT: 'Flowkit',
  SECTIONS: 'Sections',
  UTILITY: 'Utility',
} as const;

export const CAMPAIGN_COMPONENT_SPECIAL_GROUP_NAMES = {
  HUBSPOT_FORMS: 'HubSpot Forms',
  MARKETO_FORMS: 'Marketo Forms',
} as const;

export const CAMPAIGN_COMPONENT_PLACEMENT_KIND = {
  BLOCK: 'block',
  ELEMENT: 'element',
  SECTION: 'section',
} as const;

export type CampaignComponentPlacementKind =
  (typeof CAMPAIGN_COMPONENT_PLACEMENT_KIND)[keyof typeof CAMPAIGN_COMPONENT_PLACEMENT_KIND];

export type CampaignComponentKind = CampaignComponentPlacementKind | null;

type CampaignComponentGroupRule = {
  browserGroupCategory: Exclude<
    CampaignComponentGroupCategory,
    typeof CAMPAIGN_COMPONENT_GROUP_CATEGORY.OTHER
  >;
  placementKind: CampaignComponentKind;
};

/**
 * Maps component library group names to the two Campaign concepts that depend
 * on them:
 *
 * - browserGroupCategory controls where a group appears in the Component Browser.
 * - placementKind controls where instances from that group may be dropped.
 *
 * Some known groups, like Flowkit and Utility, are not Component Browser tabs,
 * but still need explicit rules so callers do not treat them as unknown groups.
 */
export const CAMPAIGN_COMPONENT_GROUP_RULES_BY_NAME: Record<
  string,
  CampaignComponentGroupRule
> = {
  [CAMPAIGN_COMPONENT_GROUP_NAMES.BLOCKS]: {
    browserGroupCategory: CAMPAIGN_COMPONENT_GROUP_CATEGORY.BLOCK,
    placementKind: CAMPAIGN_COMPONENT_PLACEMENT_KIND.BLOCK,
  },
  [CAMPAIGN_COMPONENT_GROUP_NAMES.FLOWKIT]: {
    browserGroupCategory: CAMPAIGN_COMPONENT_GROUP_CATEGORY.FLOWKIT,
    placementKind: CAMPAIGN_COMPONENT_PLACEMENT_KIND.ELEMENT,
  },
  [CAMPAIGN_COMPONENT_GROUP_NAMES.SECTIONS]: {
    browserGroupCategory: CAMPAIGN_COMPONENT_GROUP_CATEGORY.SECTION,
    placementKind: CAMPAIGN_COMPONENT_PLACEMENT_KIND.SECTION,
  },
  [CAMPAIGN_COMPONENT_GROUP_NAMES.UTILITY]: {
    browserGroupCategory: CAMPAIGN_COMPONENT_GROUP_CATEGORY.UTILITY,
    placementKind: null,
  },
  [CAMPAIGN_COMPONENT_SPECIAL_GROUP_NAMES.HUBSPOT_FORMS]: {
    browserGroupCategory: CAMPAIGN_COMPONENT_GROUP_CATEGORY.BLOCK,
    placementKind: CAMPAIGN_COMPONENT_PLACEMENT_KIND.BLOCK,
  },
  [CAMPAIGN_COMPONENT_SPECIAL_GROUP_NAMES.MARKETO_FORMS]: {
    browserGroupCategory: CAMPAIGN_COMPONENT_GROUP_CATEGORY.BLOCK,
    placementKind: CAMPAIGN_COMPONENT_PLACEMENT_KIND.BLOCK,
  },
};

const CAMPAIGN_COMPONENT_GROUP_RULE_BY_NORMALIZED_NAME = new Map(
  Object.entries(CAMPAIGN_COMPONENT_GROUP_RULES_BY_NAME).map(
    ([groupName, rule]) => [groupName.toLowerCase(), rule]
  )
);

export function getCampaignComponentGroupRule(
  groupName: string
): CampaignComponentGroupRule | null {
  const normalizedGroupName = groupName.trim().toLowerCase();
  return (
    CAMPAIGN_COMPONENT_GROUP_RULE_BY_NORMALIZED_NAME.get(normalizedGroupName) ??
    null
  );
}
